import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ErrorsFormsService } from '@bo-client/shared/form-errors/errors-forms.service';
import { GridRegistrationRequestService } from '@bo-client/activation-management/grid-registration/grid-registration-request.service';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { GridRegistrationFormBase } from '@bo-client/activation-management/grid-registration/grid-registration-form.base';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  FeedInCompensationType,
  GridRegistrationStatus,
  GridRegistrationStatusEnum,
  GridRegistrationTypeEnum,
  PowerGenerationSourceEnum
} from '../../../grid-registration/model/grid-registration.enum';
import { Observable, Subject, of } from 'rxjs';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  takeUntil,
  tap
} from 'rxjs/operators';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { AreaTypeEnum, CommonStatusEnum } from '../../../shared/models/commissioningModel';
import Utils from '@bo-client/shared/utils';
import { ConsumerModel } from '@bo-client/activation-management/grid-registration/model/consumer.model';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { StorageModel } from '@bo-client/activation-management/grid-registration/model/storage.model';
import { TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { ConnectionObjectFormV2 } from '@bo-client/shared/models/connectionObject';
import { CustomValidators } from '@bo-client/activation-management/shared/forms/validators';

@Component({
  selector: '' + 'app-grid-registration-form-feeder',
  templateUrl: './grid-registration-form-feeder.component.html',
  styleUrls: ['./grid-registration-form-feeder.component.scss']
})
export class GridRegistrationFormFeederComponent
  extends GridRegistrationFormBase
  implements OnInit, OnDestroy
{
  protected readonly GridRegistrationStatusEnum = GridRegistrationStatusEnum;
  originalModel: GridRegistrationModel;
  protected compUnsubs$: Subject<void> = new Subject<void>();
  totalPowerEEG$: Observable<number>;
  hideAdditionalGridConnectionPointInformationCard$: Observable<boolean>;
  connectionObjectData$: Observable<ConnectionObjectFormV2['value']>;
  totalConsumerStoragePower$: Observable<number>;

  constructor(
    protected authorizationService: AuthorizationService,
    protected httpHapService: HttpHapService,
    protected dataShareService: DataShareService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected toastService: ToastrService,
    protected errorsFormsService: ErrorsFormsService,
    private fb: FormBuilder,
    private createFormService: CreateFormsService,
    protected gridRegistrationService: GridRegistrationRequestService,
    protected createFormsGridRegistrationService: CreateGridRegistrationFormsService,
    private validatorsService: ValidatorsService
  ) {
    super(authorizationService, dataShareService);

    // TODO: move this into a dedicated service!?
    this.formGroup = this.fb.group(
      {
        state: [''],
        formVersion: ['v2'],
        // Contact Data
        customer: this.createFormService.createContactDataFormV2({}, true),
        invoiceContact: this.createFormService.createContactDataFormV2({}, false),
        differingInvoiceContact: [false], // (Transient) steuert ob ein abweichender Rechnungsempfänger/Anschlussnehmer vorhanden ist.
        // Anschlussobject
        connectionObject: this.createFormService.connectionObjectFormWithValidatorV2({}),
        connectionObjectAdditionalInfo: this.createFormService.connectionObjectAdditionalInfoForm({}),

        // rückfrage Felder
        installerFeedbackComment: [null],

        // Der Betriebsbereich
        companyLocation: {
          id: [null]
        },

        comment: [null],
        shortDescription: [null],
        longDescription: [null],
        externalId: [null],
        assignedTransformerStation: [null],
        gridConnectionPointId: [null],
        trId: [null],
        srId: [null],
        meLo: [null],
        maLo: [null],

        powerGenerationSource: [null, Validators.required],
        feedingPlantType: [null, Validators.required],
        isEEGExtention: [false],
        hasInverterInstalled: [false], // Ob ein Wechslerichter bereits installiert ist

        feedInManagementInfo: [null, Validators.required],
        feedInManagementSerialNumber: [null],

        directMarketing: [null],
        directMarketer: [null],

        feedInCompensationType: [
          null,
          this.dataShareService.tenantConfig.gridRegistration.showCompensationQuestion
            ? [requireFeedInCompensationType]
            : []
        ],

        hasStorage: [null],
        storages: this.fb.array([]),
        consumers: this.fb.array([]),

        existingConsumption: [null, [Validators.min(0)]], // Bezug PAV,E+
        existingProduction: [null, [Validators.min(0)]], // Max. Einspeisung PAV,E+
        installedPower: [null, [Validators.min(0)]], // Installierte Erzeugungsleistung Pinst
        newConsumption: [null, [Validators.min(0)]],
        newProduction: [null, [Validators.required, Validators.min(0)]],
        newInstalledPower: [null, [Validators.required, Validators.min(0)]],

        // Detaillierte Aufschlüsselung von Modulen / Wechselrichtern
        pvModules: this.fb.array([]),
        producers: this.fb.array([]),

        // those should be common again
        meteringConcept: this.fb.group({ id: [null] }),
        uploads: this.fb.array([]), // Dokumente

        // Bedingungen und Erklärungen akzeptiert
        acceptedConditions1: [null, this.validatorsService.checkboxRequiredValidator()],
        acceptedConditions2: [null],

        // Weitere Informationen zum Netzanschluss
        contractedConnectionPower: [null],
        connectionFuseAmplitude: [null],
        hasInstalledDevices: [null],
        existingFeeders: this.fb.array([]),

        // Für allgemeine Daten
        validityExpirationDate: [null],

        // Für externe Daten
        externalCustomerId: ['']
      },
      {
        validators: [
          CustomValidators.createPVModulePowerValidatorSync(
            this.dataShareService.tenantConfig?.gridRegistration?.allowedPowerDeviationPercent
          )
        ],
        updateOn: 'change'
      }
    );
    this.initConnectionObjectData$();
    this.hideAdditionalGridConnectionPointInformationCard$ = this.dataShareService.tenantConfig$.pipe(
      map((config) => config?.gridRegistration?.hideAdditionalGridConnectionPointInformationCard || false)
    );
  }

  ngOnDestroy(): void {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }

  // Für die Verwendung von form arrays
  get uploads(): FormArray {
    return this.formGroup.get('uploads') as FormArray;
  }

  get storages(): FormArray {
    return this.formGroup.get('storages') as FormArray;
  }

  get consumers(): FormArray {
    return this.formGroup.get('consumers') as FormArray;
  }

  get customer(): FormGroup {
    return this.formGroup.get('customer') as FormGroup;
  }

  get invoiceContact(): FormGroup {
    return this.formGroup.get('invoiceContact') as FormGroup;
  }

  get storageFormGroup() {
    return this.storages?.controls[0] as FormGroup;
  }

  get acceptConditions() {
    return this.formGroup.controls['acceptConditions'] as FormGroup;
  }

  get meteringConcept() {
    return this.formGroup.get('meteringConcept.id') as FormControl;
  }

  get gridRegistrationType(): GridRegistrationTypeEnum {
    return this.originalModel?.gridRegistrationType;
  }

  get documentsUploadInfoUIAdaptionName() {
    return 'gridRegistration.' + this.gridRegistrationType.valueOf() + '.documentsUploadInfo';
  }

  get firstConsumer(): FormGroup {
    return (this.formGroup.get('consumers') as FormArray)?.controls[0] as FormGroup;
  }

  get newInstalledPower() {
    return this.formGroup.get('newInstalledPower')?.value;
  }

  get differingInvoiceContact() {
    return this.formGroup.get('differingInvoiceContact')?.value;
  }

  get hasStorage() {
    return this.formGroup.get('hasStorage') as FormControl;
  }

  get producers() {
    return this.formGroup.controls['producers'] as FormArray;
  }

  get pvModules() {
    return this.formGroup.controls['pvModules'] as FormArray;
  }

  get directMarketing() {
    return [
      FeedInCompensationType.MISC_DIRECT_MARKETING,
      FeedInCompensationType.SUBST_DIRECT_MARKETING
    ].includes(this.formGroup.controls['feedInCompensationType']?.value);
  }

  get existingFeeders(): FormArray {
    return this.formGroup.get('existingFeeders') as FormArray;
  }

  get showDirectMarketing() {
    return (
      this.formGroup.controls['newInstalledPower']?.value >= 25 &&
      this.dataShareService.tenantConfig.gridRegistration.showDirectMarketingQuery
    );
  }

  get showFeedInCompensationTypeQuestion() {
    return (
      this.formGroup.controls['newInstalledPower']?.value <= 400 &&
      this.dataShareService.tenantConfig.gridRegistration.showCompensationQuestion
    );
  }

  public formGroup: FormGroup;

  canUpdate$: Observable<boolean>;
  disableAll$: Observable<boolean>;

  protected readonly GridRegistrationTypeEnum = GridRegistrationTypeEnum;

  selectedCompanyLocation$: Observable<CompanyLocationDto>;
  feedInCompensationType$: Observable<FeedInCompensationType>;
  powerThresholdReached$: Observable<boolean>; // Für erweiterte Bedingungen über einen Schwellenwert von 30kWp

  protected readonly AreaTypeEnum = AreaTypeEnum;

  getFormGroup(): FormGroup {
    return this.formGroup;
  }

  ngOnInit(): void {
    this.canUpdate$ = of(true);
    this.disableAll$ = of(false);
    // initialize form

    // wenn installedpower über 200 ist soll die Direktvermarktung verpflichtend aktiviert werden und das eingabefeld deaktiviert
    this.formGroup
      .get('newInstalledPower')
      .valueChanges.pipe(
        takeUntil(this.compUnsubs$),
        tap((x) => {
          if (x >= 200) {
            this.formGroup.get('directMarketing').setValue(true);
            this.formGroup.get('directMarketing').disable();
          } else if (x < 200 && x >= 25) {
            this.formGroup.get('directMarketing').enable();
          } else {
            this.formGroup.get('directMarketing').setValue(false);
          }

          // Handle Einsepsievergütung
          if (x <= 400) {
            this.formGroup
              .get('feedInCompensationType')
              .setValidators(
                this.dataShareService.tenantConfig.gridRegistration.showCompensationQuestion
                  ? [Validators.required]
                  : []
              );
          } else {
            this.formGroup.get('feedInCompensationType').clearValidators();
            this.formGroup.get('feedInCompensationType').setValue(null);
          }
        })
      )
      .subscribe();

    // register all the change events
    // immer wen hasStorage strue ist soll storages einen Eintrag bekommen, sonst leer sein+validatoren entfernen
    // storage ist ein FormArray. Es kann also mehrere Einträge geben. Ich gehe davon aus, dass es nur einen Eintrag geben kann.
    // erstellt wir das form für das array mit gridRegistrationFormService createStorageForm
    this.formGroup
      .get('hasStorage')
      .valueChanges.pipe(
        tap((x) => {
          if (x && this.storages.length === 0) {
            this.storages.push(
              this.createFormsGridRegistrationService.createStorageFormV2(
                {
                  acCoupling: false,
                  alternatingCurrent: false,
                  count: null,
                  couplingType: undefined,
                  currentType: undefined,
                  dcCoupling: false,
                  deviceTypeName: '',
                  disconnectionByIsolatedOperation: false,
                  id: null,
                  manufacturer: '',
                  networkAndDeviceProtection: false,
                  networkReplaceService: false,
                  phases: null,
                  rotaryCurrent: false,
                  storageOperationType: null,
                  usableCapacity: null
                },
                this.originalModel
              )
            );
          } else if (x === false && this.storages.length > 0) {
            this.storages.clear();
          }
        })
      )
      .subscribe();

    this.formGroup.valueChanges
      .pipe(
        tap((x) => {
          // if hasStorage is undefined we want to skip this since it is not correcty initialized
          if (x.hasStorage === undefined) {
            return;
          }

          if (
            x.hasStorage &&
            x.storages &&
            (x.storages[0].storageOperationType === 'CONSUMPTION_NO_PRODUCTION' ||
              x.storages[0].storageOperationType === 'CONSUMPTION_PRODUCTION')
          ) {
            if (this.consumers.length === 0) {
              const storageModel = new ConsumerModel('Storage');
              // we want to specify fallback values
              storageModel.count = 1;
              storageModel.manufacturer = '-';
              this.consumers.push(this.createFormsGridRegistrationService.createConsumerFormV2(storageModel));
            }
          } else {
            this.consumers.clear();
          }
        })
      )
      .subscribe();

    this.formGroup
      .get('state')
      .valueChanges.pipe(
        takeUntil(this.compUnsubs$),
        startWith(this.formGroup.get('state').value),
        tap((x) => {
          const isInstallerUser = this.authorizationService.hasOneOfThisRoles([
            'ROLE_INSTALLATEUR',
            'ROLE_ADMIN_INSTALLATEUR'
          ]);

          const isEditorUser = this.authorizationService.hasOneOfThisRoles(['ROLE_EDITOR_GRIDREGISTRATIONS']);
          const canEditAsInstaller =
            isInstallerUser &&
            [GridRegistrationStatusEnum.Incomplete, GridRegistrationStatusEnum.FeedbackNeeded].includes(
              x as CommonStatusEnum
            );
          const canEditAsEditor =
            isEditorUser &&
            [
              GridRegistrationStatusEnum.Requested,
              GridRegistrationStatusEnum.Approved,
              GridRegistrationStatusEnum.UnderReview
            ].includes(x as CommonStatusEnum);

          const canEditAsAnonUser = [
            GridRegistrationStatusEnum.Incomplete,
            GridRegistrationStatusEnum.FeedbackNeeded
          ].includes(x as CommonStatusEnum);

          const canEditAsKeyUser = this.authorizationService.hasOneOfThisRoles([
            'ROLE_KEYUSER_GRIDREGISTRATION'
          ]);
          const canEdit = canEditAsEditor || canEditAsInstaller || canEditAsKeyUser || canEditAsAnonUser;

          if (!canEdit) {
            this.disableFields();
          } else {
            this.enableFields();
          }
        })
      )
      .subscribe();
    (this.formGroup.controls.storages as FormArray<TypedForm<StorageModel>>).valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        startWith((this.formGroup.controls.storages as FormArray<TypedForm<StorageModel>>).value),
        map((storages) => {
          return storages.length > 0 ? storages[0].count : null;
        })
      )
      .subscribe({
        next: (count) => {
          if (!this.firstConsumer || count === null) return;
          (this.firstConsumer as TypedForm<ConsumerModel>).controls.count?.patchValue(count);
        }
      });
    this.totalConsumerStoragePower$ = this.formGroup.valueChanges.pipe(
      startWith(this.formGroup.value),
      map(() => {
        const formValue = this.formGroup.getRawValue();
        const power = formValue.consumers[0]?.power || 0;
        const count = formValue.storages[0]?.count || 0;
        if (!count || !power) {
          return Number.NaN;
        }
        return Number(power) * Number(count);
      })
    );

    this.selectedCompanyLocation$ = this.formGroup.get('connectionObject.orgUnitDesc').valueChanges.pipe(
      startWith(this.formGroup.get('connectionObject.orgUnitDesc').value),
      combineLatestWith(this.dataShareService.companyLocations$),
      filter(([orgUnitDesc, companyLocations]) => !!orgUnitDesc && !!companyLocations),
      map(([orgUnitDesc, companyLocations]) => {
        return companyLocations.find((x) => x.external_id === orgUnitDesc);
      }),
      // after we selected the companyLocation we want to patch the id into the form
      tap((val) => this.formGroup.patchValue({ companyLocation: { id: val.id } }, { emitEvent: false }))
    );

    /**
     * For the one of you that isn't that deep into rxjs :)
     *
     * This TypeScript code is creating an Observable totalPowerEEG$ that emits values based on changes in a form group.
     * Here's a breakdown of what the code does:
     * this.formGroup.get('newInstalledPower').valueChanges creates an Observable that emits the current value of the 'newInstalledPower' form control and every subsequent change.
     * startWith(this.formGroup.get('newInstalledPower').value) ensures that the Observable emits a value immediately with the current value of 'newInstalledPower'.
     * combineLatestWith combines the latest values from the specified Observables. In this case, it's combining the latest values from 'newInstalledPower', 'powerGenerationSource', and 'newProduction'.
     * takeUntil(this.compUnsubs$) unsubscribes from the Observable when this.compUnsubs$ emits a value. This is typically used to prevent memory leaks by unsubscribing when the component is destroyed.
     * tap((x) => console.log('totalPowerEEG$', x)) is used for side effects, in this case logging the value to the console.
     * map(([newInstalledPower, powerGenerationSource, newProduction]) => {...}) transforms the emitted value. If 'powerGenerationSource' is 'PhotoVoltaic', it returns 'newInstalledPower'. Otherwise, it returns 'newProduction'.
     */
    this.totalPowerEEG$ = this.formGroup.get('newInstalledPower').valueChanges.pipe(
      startWith(this.formGroup.get('newInstalledPower').value),
      combineLatestWith(
        this.formGroup
          .get('powerGenerationSource')
          .valueChanges.pipe(startWith(this.formGroup.get('powerGenerationSource').value)),
        this.formGroup
          .get('newProduction')
          .valueChanges.pipe(startWith(this.formGroup.get('newProduction').value))
      ),
      takeUntil(this.compUnsubs$),
      tap((x) => console.log('totalPowerEEG$', x)),
      map(([newInstalledPower, powerGenerationSource, newProduction]) => {
        return powerGenerationSource === PowerGenerationSourceEnum.PhotoVoltaic
          ? newInstalledPower
          : newProduction;
      })
    );

    this.feedInCompensationType$ = this.formGroup
      .get('feedInCompensationType')
      .valueChanges.pipe(startWith(this.formGroup.get('feedInCompensationType').value));

    this.powerThresholdReached$ = this.formGroup.get('newInstalledPower').valueChanges.pipe(
      startWith(this.formGroup.get('newInstalledPower').value),
      map((totalPower: number) => totalPower > 30),
      shareReplay(1)
    );
    this.powerThresholdReached$
      .pipe(
        takeUntil(this.compUnsubs$),
        distinctUntilChanged(),
        combineLatestWith(this.dataShareService.uiAdaptions$)
      )
      .subscribe({
        next: ([isReached, adaptions]) => {
          const control = this.formGroup.get('acceptedConditions2');

          const adaptionVisibility = adaptions.find(
            (x) => x.anchor === 'gridRegistration.powerThresholdConditions_30'
          )?.visibility;
          if (!isReached || !(adaptionVisibility === 'show' || adaptionVisibility === 'true')) {
            control.clearValidators();
            control.markAsUntouched();
            control.setValue(null);
          } else {
            control.setValidators([this.validatorsService.checkboxRequiredValidator()]);
          }
        }
      });
  }

  getModelFromForm(): GridRegistrationModel {
    // we need to map a few properties to keep the model consistent
    console.log(
      'trying to save model',
      JSON.parse(JSON.stringify(this.formGroup.value)),
      JSON.parse(JSON.stringify(this.originalModel))
    );
    const response = this.formGroup.value;
    const updatedModel = {
      ...this.originalModel,
      ...response
    };

    Utils.logDiff(this.originalModel, updatedModel);

    return updatedModel;
  }

  loadErrorsList() {}

  /**
   * Hier wird von der enthaltenden Page angefragt, ob das Formular valide ist
   * Wird z.B. vor Abschicken geprüft.
   */
  isValid() {
    console.log(this.formGroup);
    this.formGroup.markAllAsTouched();
    // valid ist nicht true, wenn die formGroup disabled ist, darum müssen wir dies hier extra abfragen.
    return this.formGroup.valid || this.formGroup.disabled;
  }

  /**
   * Dies wird bei der initialen Befüllung des Forms aufgerufen.
   * @param model
   */
  loadFormWithModel(model: GridRegistrationModel) {
    this.updateFormWithModelInternal(model);
  }

  /**
   * Und das hier z.B. nachdem etwas gespeichert wurde
   * @param model
   */
  updateFormWithModel(model: GridRegistrationModel) {
    console.log('executed updateFormWithModel: ', model);
    this.updateFormWithModelInternal(model);
  }

  private updateFormWithModelInternal(model: GridRegistrationModel) {
    console.log('Updating form with internal model', JSON.parse(JSON.stringify(model)));

    // we want to store the "old" model to patch it later
    this.originalModel = model;

    // Stellen Sie sicher, dass alle FormArrays und FormGroups initialisiert sind

    this.storages.clear();
    model.storages.forEach((storage) => {
      const storageGroup = this.createFormsGridRegistrationService.createStorageFormV2(storage, model);
      this.storages.push(storageGroup);
    });

    this.consumers.clear();
    model.consumers.forEach((consumer) => {
      const consumerGroup = this.createFormsGridRegistrationService.createConsumerFormV2(consumer);
      this.consumers.push(consumerGroup);
    });

    this.pvModules.clear();
    model.pvModules.forEach((pvModule) => {
      const pvModuleGroup = this.createFormsGridRegistrationService.createPVModuleFormSimple(pvModule);
      this.pvModules.push(pvModuleGroup);
    });

    this.producers.clear();
    model.producers.forEach((producer) => {
      const producerGroup = this.createFormsGridRegistrationService.createProducerFormSimple(producer, model);
      this.producers.push(producerGroup);
    });

    this.uploads.clear();
    model.uploads.forEach((upload) => {
      const uploadFormGroup = this.createFormService.createUploadForm(upload);
      this.uploads.push(uploadFormGroup);
    });

    this.existingFeeders.clear();
    model.existingFeeders.forEach((existingFeeder) => {
      const existingFeederFormGroup =
        this.createFormsGridRegistrationService.createExistingFeederForm(existingFeeder);
      this.existingFeeders.push(existingFeederFormGroup);
    });

    // Laden Sie jetzt das Modell in das Formular
    this.formGroup.patchValue(model, { emitEvent: true });

    // Wir leiten uns die transiente prop her, ob ein abweichender Rechnungsempfänger vorhanden ist
    this.formGroup.get('differingInvoiceContact').patchValue(!!model.invoiceContact?.salutation);

    // Datumsfelder müssen als Date-Objekte gepatcht werden
    this.formGroup
      .get('validityExpirationDate')
      .patchValue(model.validityExpirationDate ? new Date(model.validityExpirationDate) : null);
  }

  protected readonly gridRegistrationTypeEnum = GridRegistrationTypeEnum;

  resetDifferingInvoiceContactForm(checked: boolean) {
    if (!checked) {
      for (const control of Object.values(this.invoiceContact.controls)) {
        control.clearValidators();
        control.patchValue(null);
      }
    }
  }

  protected readonly CommonStatusEnum = CommonStatusEnum;
  protected readonly GridRegistrationStatus = GridRegistrationStatus;

  private initConnectionObjectData$() {
    this.connectionObjectData$ = this.formGroup.controls.connectionObject.valueChanges.pipe(
      takeUntil(this.compUnsubs$),
      startWith(this.formGroup.controls.connectionObject.value),
      shareReplay(1)
    );
  }
}
const requireFeedInCompensationType = (control: FormControl): ValidationErrors => {
  if (
    control.parent &&
    control.parent.get('newInstalledPower') &&
    control.parent.get('newInstalledPower').value <= 400 &&
    control.value === null
  ) {
    return { required: true };
  }
  return null;
};
